/** Build structured on-page issue rows for URL detail Technical section. */

export interface OnpageIssueView {
  /**
   * The DataForSEO check name, or `resource:<normalised message>` for a free-text
   * resource error.
   *
   * This is the join key against the domain rollup's `siteWideCodes`, which is
   * how one page's panel learns that three of its eight issues are the theme's.
   * It is not a second classification: the code is simply the name of the check
   * this block already branched on, and the resource form is normalised the same
   * way the backend normalises it (digits to `#`), so "…(2 scripts)" and
   * "…(3 scripts)" are one issue rather than two.
   */
  code: string;
  title: string;
  details: string[];
}

interface StoredPageResource {
  url: string;
  resourceType: 'script' | 'stylesheet';
}

interface PageResourcesPayload {
  renderBlockingScripts?: StoredPageResource[];
  renderBlockingStylesheets?: StoredPageResource[];
  metaCounts?: {
    renderBlockingScripts?: number;
    renderBlockingStylesheets?: number;
  };
  totals?: { scripts?: number; stylesheets?: number };
}

function pageDataFromAnalysis(analysis: unknown): Record<string, unknown> | null {
  const a = analysis as { rawData?: { pageData?: unknown } } | null;
  const pd = a?.rawData?.pageData;
  return pd && typeof pd === 'object' ? (pd as Record<string, unknown>) : null;
}

function meta(pageData: Record<string, unknown> | null): Record<string, unknown> {
  const m = pageData?.['meta'];
  return m && typeof m === 'object' ? (m as Record<string, unknown>) : {};
}

function checks(pageData: Record<string, unknown> | null): Record<string, boolean> {
  const c = pageData?.['checks'];
  if (!c || typeof c !== 'object') return {};
  const out: Record<string, boolean> = {};
  for (const [k, v] of Object.entries(c as Record<string, unknown>)) {
    if (typeof v === 'boolean') out[k] = v;
  }
  return out;
}

function stringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((v) => String(v).trim()).filter(Boolean);
}

function pageResourcesFromAnalysis(analysis: unknown): PageResourcesPayload | null {
  const pr = (analysis as { rawData?: { pageResources?: unknown } } | null)?.rawData?.pageResources;
  return pr && typeof pr === 'object' ? (pr as PageResourcesPayload) : null;
}

function renderBlockingResourceDetails(analysis: unknown): string[] {
  const payload = pageResourcesFromAnalysis(analysis);
  if (!payload) return [];

  const details: string[] = [];
  const scripts = payload.renderBlockingScripts ?? [];
  const styles = payload.renderBlockingStylesheets ?? [];

  for (const row of scripts) {
    if (row?.url) details.push(`Script: ${row.url}`);
  }
  for (const row of styles) {
    if (row?.url) details.push(`Stylesheet: ${row.url}`);
  }

  const metaScripts = payload.metaCounts?.renderBlockingScripts;
  const metaStyles = payload.metaCounts?.renderBlockingStylesheets;
  if (
    typeof metaScripts === 'number' &&
    scripts.length > 0 &&
    scripts.length < metaScripts
  ) {
    details.push(
      `DataForSEO reports ${metaScripts} blocking script${metaScripts === 1 ? '' : 's'}; showing ${scripts.length} earliest-loaded match${scripts.length === 1 ? '' : 'es'}.`
    );
  }
  if (
    typeof metaStyles === 'number' &&
    styles.length > 0 &&
    styles.length < metaStyles
  ) {
    details.push(
      `DataForSEO reports ${metaStyles} blocking stylesheet${metaStyles === 1 ? '' : 's'}; showing ${styles.length} earliest-loaded match${styles.length === 1 ? '' : 'es'}.`
    );
  }

  if (details.length === 0 && (metaScripts || metaStyles)) {
    details.push(
      'Render-blocking counts are set on the page, but no matching script/stylesheet URLs were returned for this crawl.'
    );
  }

  return details;
}

/**
 * Stored issue strings that are not issues — the backend taxonomy's
 * `NOT_SURFACED_STORED_ISSUE_TEXTS`, copied because the frontend cannot import
 * across the workspace boundary. `technical-issue-rollup.test.ts` reads this
 * file and fails if the two drift.
 *
 * The crawler still writes both (its stored strings are fingerprinted history,
 * see TECHNICAL_DEBT §0be): `has_meta_title` is DataForSEO's non-standard
 * `<meta name="title">`, not the `<title>` element, and an image title attribute
 * is a tooltip nothing ranks on. Their check branches are gone from
 * `checkIssueViews` for the same reason.
 */
export const NOT_SURFACED_STORED_ISSUE_TEXTS: readonly string[] = [
  'page missing meta title tag',
  'images missing title attributes (accessibility issue)'
];

/** A stored issue list with the not-an-issue strings removed. */
export function surfacedStoredIssues(items: readonly string[] | null | undefined): string[] {
  return (items ?? []).filter(
    (text) => !NOT_SURFACED_STORED_ISSUE_TEXTS.includes(String(text).trim().toLowerCase())
  );
}

/** Must match `normalizeTechnicalIssueText` in the backend taxonomy exactly. */
function resourceCode(message: string): string {
  return `resource:${String(message).toLowerCase().replace(/\d+/g, '#').replace(/\s+/g, ' ').trim()}`;
}

/**
 * One row per resource check, however many times the crawler raised it on the page.
 *
 * The crawler reports each occurrence separately: on SlideSpeak's 25 baseline pages "The closing tag
 * and the currently open tag do not match." came back 1,033 times (62 on one page), and each was its
 * own row on the card, its own tick and its own line in the export. A check is one finding; how often
 * it fired is a detail of it. The first occurrence's position is kept, in the order the crawler gave.
 */
function resourceErrorViews(pageData: Record<string, unknown> | null, kind: 'errors' | 'warnings'): OnpageIssueView[] {
  const re = pageData?.['resource_errors'];
  if (!re || typeof re !== 'object') return [];
  const list = (re as Record<string, unknown>)[kind];
  if (!Array.isArray(list)) return [];
  const byCode = new Map<string, { view: OnpageIssueView; position: string | null; times: number }>();
  for (const item of list) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    const message = typeof row['message'] === 'string' ? row['message'].trim() : '';
    if (!message) continue;
    const code = resourceCode(message);
    const seen = byCode.get(code);
    if (seen) {
      seen.times += 1;
      continue;
    }
    const line = Number(row['line']);
    const column = Number(row['column']);
    const position =
      Number.isFinite(line) && line > 0
        ? `line ${line}${Number.isFinite(column) && column > 0 ? `, column ${column}` : ''}`
        : null;
    byCode.set(code, { view: { code, title: message, details: [] }, position, times: 1 });
  }
  return [...byCode.values()].map(({ view, position, times }) => {
    if (times === 1) {
      return { ...view, details: position ? [position.charAt(0).toUpperCase() + position.slice(1)] : [] };
    }
    const count = `Found ${times.toLocaleString('en-US')} times on this page`;
    return { ...view, details: [position ? `${count}; the first at ${position}.` : `${count}.`] };
  });
}

/**
 * The page half of the "low content rate" explanation.
 *
 * DataForSEO already measured all of it: `plain_text_rate`,
 * `plain_text_word_count` and the rendered `size`. Stating them together is the
 * whole correction — "2,425 words, 8% of a 181 KB document" cannot be read as
 * thin copy, while the bare check name can and did.
 *
 * The domain half arrives as a `sharedCause` note composed server-side; this
 * never invents one, so a page with no rollup loaded simply shows its own
 * numbers rather than a guess about the site.
 */
function lowContentRateDetails(
  pageData: Record<string, unknown> | null,
  sharedNote: string | null
): string[] {
  const content = (meta(pageData)['content'] ?? {}) as Record<string, unknown>;
  const rate = Number(content['plain_text_rate']);
  const words = Number(content['plain_text_word_count']);
  const size = Number(pageData?.['size']);

  const out: string[] = [];
  if (Number.isFinite(rate) && Number.isFinite(words)) {
    const pct = `${(rate * 100).toFixed(1)}%`;
    const doc = Number.isFinite(size) && size > 0
      ? ` of a ${Math.round(size / 1024).toLocaleString()} KB document`
      : '';
    out.push(`${words.toLocaleString()} words — ${pct} text${doc}.`);
  }
  if (sharedNote) out.push(sharedNote);
  return out;
}

function checkIssueViews(
  pageData: Record<string, unknown> | null,
  analysis: unknown,
  sharedCauses: Record<string, string>
): {
  critical: OnpageIssueView[];
  warnings: OnpageIssueView[];
} {
  const c = checks(pageData);
  const m = meta(pageData);
  const critical: OnpageIssueView[] = [];
  const warnings: OnpageIssueView[] = [];

  const pushCritical = (code: string, title: string, details: string[] = []) =>
    critical.push({ code, title, details });
  const pushWarning = (code: string, title: string, details: string[] = []) =>
    warnings.push({ code, title, details });

  if (c['https_to_http_links']) {
    pushCritical('https_to_http_links', 'HTTPS page contains HTTP links', [
      'Mixed content / insecure links on an HTTPS URL.'
    ]);
  }
  if (c['has_render_blocking_resources']) {
    const scripts = Number(m['render_blocking_scripts_count']);
    const styles = Number(m['render_blocking_stylesheets_count']);
    const resourceDetails = renderBlockingResourceDetails(analysis);
    const details: string[] = resourceDetails.length
      ? resourceDetails
      : [
          ...(Number.isFinite(scripts)
            ? [`${scripts} render-blocking script${scripts === 1 ? '' : 's'}`]
            : []),
          ...(Number.isFinite(styles)
            ? [`${styles} render-blocking stylesheet${styles === 1 ? '' : 's'}`]
            : []),
          'Re-run OnPage analysis to fetch per-file URLs (Resources API).'
        ];
    pushCritical('has_render_blocking_resources', 'Page has render-blocking resources', details);
  }
  if (c['duplicate_meta_tags']) {
    const tags = stringList(m['duplicate_meta_tags']);
    pushCritical(
      'duplicate_meta_tags',
      'Duplicate meta tags',
      tags.length ? tags.map((t) => `Duplicate: ${t}`) : ['Duplicate meta tag types detected.']
    );
  }
  if (c['no_image_alt']) pushCritical('no_image_alt', 'Images missing alt text');
  if (c['is_4xx_code']) pushCritical('is_4xx_code', 'Page returns 4xx status code');
  if (c['is_5xx_code']) pushCritical('is_5xx_code', 'Page returns 5xx status code');
  if (c['is_broken']) pushCritical('is_broken', 'Page is broken (error status code)');
  if (c['high_loading_time']) pushCritical('high_loading_time', 'High loading time (>3s)');
  if (c['high_waiting_time']) pushCritical('high_waiting_time', 'High waiting time (>1.5s)');
  if (c['size_greater_than_3mb']) pushCritical('size_greater_than_3mb', 'Page size exceeds 3MB');
  if (c['no_h1_tag']) pushCritical('no_h1_tag', 'Page missing H1 tag');
  if (c['title_too_long']) pushCritical('title_too_long', 'Page title too long (>65 characters)');
  if (c['title_too_short']) pushCritical('title_too_short', 'Page title too short (<30 characters)');
  if (c['no_title']) pushCritical('no_title', 'Page missing title tag');
  if (c['no_description']) pushCritical('no_description', 'Page missing meta description');
  if (c['no_favicon']) pushCritical('no_favicon', 'Page missing favicon');
  if (c['duplicate_title_tag']) pushCritical('duplicate_title_tag', 'Page has multiple title tags');
  if (c['irrelevant_title']) pushCritical('irrelevant_title', 'Page title irrelevant to content');
  if (c['irrelevant_description'])
    pushCritical('irrelevant_description', 'Page description irrelevant to content');
  if (c['low_content_rate']) {
    pushCritical(
      'low_content_rate',
      'Low content rate (<10% text)',
      lowContentRateDetails(pageData, sharedCauses['low_content_rate'] ?? null)
    );
  }
  if (c['low_character_count'])
    pushCritical('low_character_count', 'Low character count (<1024 characters)');
  if (c['low_readability_rate'])
    pushCritical('low_readability_rate', 'Low readability score (Flesch-Kincaid <15)');

  if (c['deprecated_html_tags']) {
    const tags = stringList(m['deprecated_tags']);
    pushWarning(
      'deprecated_html_tags',
      'Deprecated HTML tags',
      tags.length ? tags.map((t) => `Tag: ${t}`) : ['Deprecated HTML elements detected.']
    );
  }
  if (c['frame']) {
    pushWarning('frame', 'Page contains an iframe', [
      'The crawl renders JavaScript, so this includes iframes a third-party script injects (chat, video, tag manager). Act only if your template adds it and the framed content needs to rank.'
    ]);
  }
  if (!c['has_micromarkup']) {
    pushWarning('has_micromarkup', 'Page missing structured data (schema / microdata)');
  }

  return { critical, warnings };
}

/**
 * The last-resort path, for an analysis with no structured `rawData`.
 *
 * The code falls back to the normalised message. That will not join against a
 * check-named `siteWideCodes` entry, so such a page keeps its whole list rather
 * than having part of it hoisted — the pre-split behaviour, which is the right
 * way to degrade: showing an issue that could have been lifted is a smaller
 * error than hiding one that could not.
 */
function fallbackFromStrings(items: string[]): OnpageIssueView[] {
  return surfacedStoredIssues(items).map((title) => ({ code: resourceCode(title), title, details: [] }));
}

/**
 * Prefer structured raw crawl data; fall back to persisted string lists.
 *
 * `sharedCauses` maps an issue code to the domain-level sentence behind it —
 * `TechnicalRollupService`'s payload, passed in rather than fetched here so this
 * module stays pure and a page with no rollup renders its own numbers unchanged.
 */
export function buildOnpageTechnicalIssueViews(
  analysis: unknown,
  sharedCauses: Record<string, string> = {}
): {
  critical: OnpageIssueView[];
  warnings: OnpageIssueView[];
} {
  const pageData = pageDataFromAnalysis(analysis);
  if (pageData) {
    const fromChecks = checkIssueViews(pageData, analysis, sharedCauses);
    return {
      critical: [...resourceErrorViews(pageData, 'errors'), ...fromChecks.critical],
      warnings: [...resourceErrorViews(pageData, 'warnings'), ...fromChecks.warnings]
    };
  }

  const a = analysis as { criticalIssues?: string[]; warnings?: string[] } | null;
  return {
    critical: fallbackFromStrings(Array.isArray(a?.criticalIssues) ? a!.criticalIssues! : []),
    warnings: fallbackFromStrings(Array.isArray(a?.warnings) ? a!.warnings! : [])
  };
}
